import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { message, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CalendarOutlined,
  ProjectOutlined,
  SolutionOutlined,
  BulbOutlined,
  TeamOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  TranslationOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import styles from './aisidebar.module.less';
import '@/pages/analyse/components/GeminiContent.less';
import { downloadLog, getHistory, getSummary, translateText } from '@/api/subtitles';
import { downloadFile } from '@/utils';
import { stripMarkdownDividers } from '@/utils/common';


const LANGUAGE_OPTIONS = [
  { code: 'default', flag: '\u{1F310}', name: 'Default' },
  { code: 'zh_hans', flag: '\u{1F1E8}\u{1F1F3}', name: '\u7B80\u4F53\u4E2D\u6587' },
  { code: 'ja', flag: '\u{1F1EF}\u{1F1F5}', name: '\u65E5\u672C\u8A9E' },
  { code: 'en', flag: '\u{1F1FA}\u{1F1F8}', name: 'English' },
];

interface AISidebarProps {
  segmentId?: string;
  fileMergeId?: string;
  title?: string;
  gmtCreate?: string;
}

const AISidebar: React.FC<AISidebarProps> = ({ segmentId, fileMergeId, title: propTitle, gmtCreate }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const defaultOutputLang = useMemo(() => {
    const map: Record<string, string> = {
      'zh-CN': 'zh_hans',
      'zh-TW': 'zh_hant',
      'en-US': 'en',
      'ja-JP': 'ja',
      en: 'en',
      ja: 'ja',
      zh: 'zh_hans',
    };
    return map[language] || 'en';
  }, [language]);

  const [panelVisible, setPanelVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'translation'>('summary');
  const [selectedLang, setSelectedLang] = useState('default');
  const [translationLang, setTranslationLang] = useState('default');
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<API.SummaryDetails[]>([]);
  const [translationContent, setTranslationContent] = useState<{ speaker_id: string; transcription: string }[] | null>(
    null,
  );
  const [translationLoading, setTranslationLoading] = useState(false);
  // Insights view state
  const [insightsViewOpen, setInsightsViewOpen] = useState(false);
  const [currentInsight, setCurrentInsight] = useState<API.SummaryDetails | null>(null);
  const [currentTemplateKey, setCurrentTemplateKey] = useState<string>('');

  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Get current language based on active tab
  const currentLang = activeTab === 'translation' ? translationLang : selectedLang;

  const selectedLangOption = useMemo(
    () => LANGUAGE_OPTIONS.find((l) => l.code === currentLang) || LANGUAGE_OPTIONS[0],
    [currentLang],
  );

  // Resolve actual language code for API calls ('default' -> browser language)
  const resolvedLang = useMemo(
    () => (selectedLang === 'default' ? defaultOutputLang : selectedLang),
    [selectedLang, defaultOutputLang],
  );

  // Disable download button when on summary tab of a transcription record
  // (summary tab has no .transcript-row elements, so download would fail silently)
  const downloadDisabled = activeTab === 'summary' && !!fileMergeId;

  // Handle language change for translation tab
  const handleTranslationLangChange = async (langCode: string) => {
    setTranslationLang(langCode);

    // If selecting 'default', clear translated content to show the prompt
    if (langCode === 'default') {
      setTranslationContent(null);
      return;
    }

    if (!segmentId) {
      message.error(t('ai.noSegmentSelected'));
      return;
    }

    setTranslationLoading(true);
    try {
      const res = await translateText({
        user_subtitle_segment_id: segmentId,
        target_lang: langCode,
      });
      setTranslationContent(res.data.data.generate_transcription_list);
    } catch (error) {
      console.error('Failed to translate transcript:', error);
      message.error(t('ai.fetchFailed'));
    } finally {
      setTranslationLoading(false);
    }
  };

  // Click outside to close panel and language dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (langBtnRef.current && !langBtnRef.current.contains(target)) {
        setLangDropdownOpen(false);
      }

      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setPanelVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch history
  useEffect(() => {
    if (!segmentId) return;
    getHistory({ user_subtitle_segment_id: segmentId }).then((res) => {
      setHistory(res.data.data.summary_details_list);
    });
  }, [segmentId]);

  // Scroll to latest history item when new content is added
  useEffect(() => {
    if (history.length > 0 && contentRef.current && panelVisible && activeTab === 'summary') {
      // Scroll to show the latest history item (smooth scroll)
      const historyItems = contentRef.current.querySelectorAll(`.${styles.historyItem}`);
      const lastItem = historyItems[historyItems.length - 1];
      if (lastItem) {
        lastItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [history.length, panelVisible, activeTab]);

  const fetchSummary = async (type: string, showInsightsView = true) => {
    if (!segmentId) {
      message.error(t('ai.noSegmentSelected'));
      return;
    }

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(t('errors.requestTimeout'))), 30000);
    });

    try {
      const result = await Promise.race([
        getSummary({
          user_subtitle_segment_id: segmentId,
          target_lang: resolvedLang,
          summary_prompt_type_enum: type,
        }),
        timeoutPromise,
      ]);

      const res = result as any;
      const summaryDetails = res.data.data.summary_details;
      setHistory((prev) => [...prev, summaryDetails]);

      // Show insights view with the new result
      if (showInsightsView) {
        setCurrentInsight(summaryDetails);
        setCurrentTemplateKey(type);
        setInsightsViewOpen(true);
      }
    } catch (error) {
      if ((error as Error).message === t('errors.requestTimeout')) {
        message.error(t('ai.requestTimeout'));
      } else {
        const errorMsg = (error as any)?.response?.data?.message;
        if (errorMsg?.includes('Insufficient')) {
          message.error(t('ai.insufficientContent'));
        } else {
          message.error(t('ai.fetchFailed'));
        }
      }
    }
  };

  const handleTemplateClick = (key: string) => {
    if (loading) return;
    setLoading(true);
    setCurrentTemplateKey(key);
    setCurrentInsight(null); // Clear previous insight
    setInsightsViewOpen(true); // Show insights view immediately with loading
    fetchSummary(key).finally(() => {
      setLoading(false);
    });
  };

  // Close insights view and return to main panel
  const closeInsightsView = () => {
    setInsightsViewOpen(false);
    setCurrentInsight(null);
  };

  // Retry the current template
  const retryInsights = () => {
    if (loading || !currentTemplateKey) return;
    setLoading(true);
    fetchSummary(currentTemplateKey).finally(() => {
      setLoading(false);
    });
  };

  // Close entire panel from insights view
  const closeInsightsPanel = () => {
    closeInsightsView();
    setPanelVisible(false);
  };

  // Get template title by key
  const getTemplateTitle = (key: string) => {
    const template = templates.find((t) => t.key === key);
    return template?.title || t('ai.meetingType.insights');
  };

  const togglePanel = () => {
    setPanelVisible((prev) => !prev);
  };

  const templates = [
    {
      key: 'STANDARD',
      title: t('ai.meetingType.insights'),
      description: t('ai.meetingType.insightsDesc'),
      loadingAction: t('ai.loadingAction.insights'),
      icon: <CalendarOutlined />,
    },
    {
      key: 'PROJECT_SCHEDULE',
      title: t('ai.meetingType.project'),
      description: t('ai.meetingType.projectDesc'),
      loadingAction: t('ai.loadingAction.project'),
      icon: <ProjectOutlined />,
    },
    {
      key: 'DECISION_MAKING',
      title: t('ai.meetingType.decision'),
      description: t('ai.meetingType.decisionDesc'),
      loadingAction: t('ai.loadingAction.decision'),
      icon: <SolutionOutlined />,
    },
    {
      key: 'BRAINSTORMING_IDEAS',
      title: t('ai.meetingType.brainstorming'),
      description: t('ai.meetingType.brainstormingDesc'),
      loadingAction: t('ai.loadingAction.brainstorming'),
      icon: <BulbOutlined />,
    },
    {
      key: 'CUSTOMER_EXTERNAL',
      title: t('ai.meetingType.customer'),
      description: t('ai.meetingType.customerDesc'),
      loadingAction: t('ai.loadingAction.customer'),
      icon: <TeamOutlined />,
    },
  ];

  // Get loading action text based on current template
  const getLoadingAction = (key: string) => {
    const template = templates.find((tmpl) => tmpl.key === key);
    return template?.loadingAction || t('ai.loadingAction.insights');
  };

  return (
    <>
      {/* Floating Button */}
      <button ref={buttonRef} className={styles.floatingButton} onClick={togglePanel} type="button">
        <svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            // eslint-disable-next-line max-len
            d="M23.8918 9.16014L23.5835 9.86724C23.358 10.3849 22.642 10.3849 22.4164 9.86724L22.1082 9.16014C21.5589 7.89934 20.5694 6.89551 19.3346 6.34635L18.385 5.92403C17.8716 5.69566 17.8716 4.94851 18.385 4.72015L19.2815 4.32143C20.548 3.75814 21.5553 2.71716 22.0951 1.41354L22.4116 0.649413C22.6323 0.116862 23.3678 0.116862 23.5882 0.649413L23.9048 1.41354C24.4448 2.71716 25.452 3.75814 26.7185 4.32143L27.6149 4.72015C28.1284 4.94851 28.1284 5.69566 27.6149 5.92403L26.6654 6.34635C25.4306 6.89551 24.4411 7.89934 23.8918 9.16014ZM13 1.5C6.09644 1.5 0.5 7.09644 0.5 14C0.5 15.9961 0.96786 17.8829 1.79991 19.5567C1.90633 19.7708 1.94125 20.0143 1.88938 20.2477L0.840886 24.966C0.682111 25.6805 1.3195 26.3179 2.034 26.1591L6.75223 25.1106C6.98563 25.0588 7.22918 25.0937 7.44328 25.2001C9.11712 26.0322 11.0039 26.5 13 26.5C19.9035 26.5 25.5 20.9035 25.5 14C25.5 13.9192 25.4992 13.8386 25.4977 13.7582C25.4846 13.068 24.8566 12.5777 24.1711 12.6591L24.1176 12.6655C23.4615 12.7434 23 13.3394 23 14C23 19.5229 18.5229 24 13 24C11.3319 24 9.7281 23.5931 8.29285 22.8256L7.80271 22.5635C7.59187 22.4507 7.3476 22.4173 7.11419 22.4691L4.0995 23.1391C3.9566 23.1708 3.82912 23.0433 3.86088 22.9004L4.53081 19.8857C4.58268 19.6523 4.54921 19.4081 4.43646 19.1972L4.17436 18.7071C3.40686 17.2719 3 15.6681 3 14C3 8.47715 7.47715 4 13 4C13.4317 4 13.8565 4.02728 14.273 4.08013C14.9579 4.16704 15.6394 3.77274 15.8114 3.10414C15.9834 2.43557 15.5812 1.74755 14.8987 1.64346C14.2791 1.54894 13.6449 1.5 13 1.5ZM9.45545 15.2274C9.2296 14.575 8.69036 14 8 14C7.30964 14 6.73696 14.5653 6.87341 15.242C7.44941 18.0988 9.97349 20.25 13 20.25C16.0265 20.25 18.5506 18.0988 19.1266 15.242C19.263 14.5653 18.6904 14 18 14C17.3096 14 16.7704 14.575 16.5445 15.2274C16.0362 16.6957 14.6413 17.75 13 17.75C11.3587 17.75 9.96378 16.6957 9.45545 15.2274Z"
            fill="url(#paint0_linear_aisidebar)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_aisidebar"
              x1="27.375"
              y1="26.5"
              x2="9.78678"
              y2="-4.64196"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#2D92FF" />
              <stop offset="1" stopColor="#DE30FF" />
            </linearGradient>
          </defs>
        </svg>
      </button>

      {/* Template Panel */}
      <div ref={panelRef} className={`${styles.templatePanel} ${panelVisible ? styles.show : ''}`}>
        {/* Main Panel Content - hidden when insights view is open */}
        {!insightsViewOpen && (
          <>
            {/* Panel Header */}
            <div className={styles.panelHeader}>
              {/* Language Selector */}
              <button
                ref={langBtnRef}
                className={`${styles.languageBtn} ${langDropdownOpen ? styles.open : ''}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLangDropdownOpen((prev) => !prev);
                }}
              >
                <span className={styles.langTooltip}>
                  {t('ai.outputLanguage')}: {selectedLangOption.name}
                </span>
                <span className={styles.langFlag}>{selectedLangOption.flag}</span>
                <svg className={styles.dropdownArrow} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {langDropdownOpen && (
                  <div className={styles.languageDropdownMenu}>
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <div
                        key={lang.code}
                        className={`${styles.languageMenuItem} ${currentLang === lang.code ? styles.selected : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeTab === 'translation') {
                            handleTranslationLangChange(lang.code);
                          } else {
                            setSelectedLang(lang.code);
                          }
                          setLangDropdownOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (activeTab === 'translation') {
                              handleTranslationLangChange(lang.code);
                            } else {
                              setSelectedLang(lang.code);
                            }
                            setLangDropdownOpen(false);
                          }
                        }}
                      >
                        <span className={styles.langMenuFlag}>{lang.flag}</span>
                        <span className={styles.langMenuName}>{lang.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </button>

              {/* Download Button */}
              <Tooltip
                title={activeTab === 'translation' ? t('ai.downloadTranslationTooltip') : t('ai.downloadTooltip')}
                placement="bottom"
              >
                <button
                  className={`${styles.panelDownloadBtn} ${downloadDisabled ? styles.disabled : ''}`}
                  type="button"
                  aria-disabled={downloadDisabled}
                  onClick={() => {
                    if (downloadDisabled) return;
                    const date = gmtCreate ? dayjs(gmtCreate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
                    const fileName = propTitle ? `${propTitle}-${date}.txt` : `transcription-${date}.txt`;

                    if (fileMergeId) {
                      // 转录记录：从 DOM 中提取转录内容
                      const contentEl = contentRef.current;
                      if (!contentEl) return;
                      const rows = contentEl.querySelectorAll('.transcript-row');
                      if (rows.length === 0) {
                        message.warning(t('ai.noContent'));
                        return;
                      }
                      const lines: string[] = [];
                      if (propTitle) {
                        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        const timeStr = gmtCreate ? dayjs(gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '';
                        lines.push(propTitle);
                        if (timeStr) {
                          lines.push(`${timeStr}(${zone})`);
                        }
                        lines.push('');
                      }
                      rows.forEach((row) => {
                        const speaker = row.querySelector('.speaker-column span');
                        const text = row.querySelector('.content-column .source-text');
                        const speakerName = speaker?.textContent?.trim() || '';
                        const content = text?.textContent?.trim() || '';
                        const rowLines: string[] = [];
                        if (speakerName) rowLines.push(speakerName);
                        if (content) rowLines.push(content);
                        if (rowLines.length > 0) lines.push(rowLines.join('\n'));
                      });
                      const blob = new Blob([lines.join('\n\n')], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = fileName;
                      a.click();
                      URL.revokeObjectURL(url);
                    } else if (segmentId) {
                      // 非转录记录：调用导出字幕 API
                      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                      downloadLog({ user_subtitle_segment_id: segmentId, zone }).then((res) => {
                        if (res.data.data) {
                          downloadFile(res.data.data, fileName);
                        }
                      });
                    }
                  }}
                >
                  <DownloadOutlined style={{ fontSize: 14, color: '#6b7280' }} />
                </button>
              </Tooltip>

              {/* Close Button */}
              <button className={styles.panelCloseBtn} type="button" onClick={() => setPanelVisible(false)}>
                <CloseOutlined style={{ fontSize: 14, color: '#6b7280' }} />
              </button>

              <div className={styles.panelTitle}>{t('ai.agentTitle')}</div>
            </div>

            {/* Feature Tabs - fixed position, only show when fileMergeId exists */}
            {fileMergeId && (
              <div className={styles.featureTabs}>
                <button
                  type="button"
                  className={`${styles.featureTab} ${activeTab === 'summary' ? styles.active : ''}`}
                  onClick={() => setActiveTab('summary')}
                >
                  {t('ai.tabSummarize')}
                </button>
                <button
                  type="button"
                  className={`${styles.featureTab} ${activeTab === 'translation' ? styles.active : ''}`}
                  onClick={() => setActiveTab('translation')}
                >
                  {t('ai.tabTranslation')}
                </button>
              </div>
            )}

            {/* Panel Content */}
            <div className={styles.panelContent} ref={contentRef}>
              {/* Summary Content - Template Cards */}
              <div style={{ display: activeTab === 'summary' ? 'block' : 'none' }}>
                {templates.map((item) => (
                  <div
                    key={item.key}
                    className={`${styles.templateItem} ${loading ? styles.disabled : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!loading) handleTemplateClick(item.key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loading) handleTemplateClick(item.key);
                    }}
                  >
                    <div className={styles.templateHeader}>
                      <div className={styles.templateIcon}>{item.icon}</div>
                      <div className={styles.templateInfo}>
                        <div className={styles.templateName}>{item.title}</div>
                        <div className={styles.templateUseCases}>{item.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Translation Content */}
              <div style={{ display: activeTab === 'translation' ? 'block' : 'none' }}>
                {translationLoading && (
                  <div className={styles.transcriptionWrapper}>
                    <div className="gemini-content">
                      <div className="content-container">
                        <div className="markdown-content">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="transcript-row">
                              <div className="speaker-column">
                                <div className={`${styles.skeletonCircle} speaker-avatar`} />
                                <div className={styles.skeletonText} style={{ width: 64 }} />
                              </div>
                              <div className="content-column">
                                <div className={styles.skeletonText} style={{ width: '92%' }} />
                                <div className={styles.skeletonText} style={{ width: '76%', marginTop: 6 }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!translationLoading && translationContent && (
                  <div className={styles.transcriptionWrapper}>
                    <div className="gemini-content">
                      <div className="content-container">
                        <div className="markdown-content">
                          {(() => {
                            const speakerMap = new Map<string, string>();
                            let idx = 1;
                            for (const item of translationContent) {
                              const id = item.speaker_id || '';
                              if (id && !speakerMap.has(id)) {
                                speakerMap.set(id, `Speaker${idx}`);
                                idx += 1;
                              }
                            }
                            return translationContent.map((item, index) => {
                              const displayName = speakerMap.get(item.speaker_id || '') || t('meeting.unknownSpeaker');
                              return (
                                <div key={index} className="transcript-row">
                                  <div className="speaker-column">
                                    <div className="speaker-avatar">{displayName.charAt(0).toUpperCase()}</div>
                                    <span>{displayName}</span>
                                  </div>
                                  <div className="content-column">
                                    <div className="source-text">{item.transcription}</div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!translationLoading && !translationContent && (
                  <div className={styles.translateOnlySection}>
                    <div className={styles.translateOnlyIcon}>
                      <TranslationOutlined style={{ fontSize: 32, color: 'white' }} />
                    </div>
                    <div className={styles.translateOnlyTitle}>{t('ai.translateTitle')}</div>
                    <div className={styles.translateOnlyDesc}>
                      <span style={{ color: '#667eea' }}>{t('ai.translateLangHint')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hint Footer */}
            {activeTab === 'summary' && (
              <div className={styles.panelHint}>
                <div className={styles.hintText}>
                  <InfoCircleOutlined style={{ fontSize: 16, color: '#667eea' }} />
                  <span>{t('ai.hintSummary')}</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Insights View - shown when a template is clicked */}
        {insightsViewOpen && (
          <div className={styles.insightsView} onClick={(e) => e.stopPropagation()}>
            {/* Insights Header */}
            <div className={styles.insightsHeader}>
              <button
                type="button"
                className={styles.insightsHeaderBtn}
                onClick={closeInsightsView}
                title={t('ai.back')}
                disabled={loading}
              >
                <ArrowLeftOutlined style={{ fontSize: 14, color: '#6b7280' }} />
              </button>
              <div className={styles.insightsHeaderTitle}>{getTemplateTitle(currentTemplateKey)}</div>
              <button
                type="button"
                className={styles.insightsHeaderBtn}
                onClick={retryInsights}
                title={t('ai.retry')}
                disabled={loading || !currentInsight}
              >
                {loading ? <Spin size="small" /> : <ReloadOutlined style={{ fontSize: 14, color: '#6b7280' }} />}
              </button>
              <button
                type="button"
                className={styles.insightsHeaderBtn}
                onClick={closeInsightsPanel}
                title={t('ai.close')}
                style={{ marginLeft: 'auto' }}
              >
                <CloseOutlined style={{ fontSize: 14, color: '#6b7280' }} />
              </button>
            </div>

            {/* Insights Body */}
            <div className={styles.insightsBody}>
              {/* 加载状态：首次生成 (!currentInsight) 或重试 (currentInsight 已存在但 loading) 都展示，
                  避免重试时旧内容直接被新内容替换的突兀感 */}
              {loading && (
                <div className={styles.insightsLoading}>
                  <div className={styles.insightsLoadingIcon}>
                    <BulbOutlined />
                  </div>
                  <div className={styles.insightsLoadingTitle}>
                    {t('ai.generatingTitle')}
                    <span className={styles.insightsLoadingDots}>
                      <span className={styles.insightsLoadingDot} />
                      <span className={styles.insightsLoadingDot} />
                      <span className={styles.insightsLoadingDot} />
                    </span>
                  </div>
                  <div className={styles.insightsLoadingDesc}>
                    {t('ai.generatingDescPrefix')}
                    {getLoadingAction(currentTemplateKey)}
                  </div>
                </div>
              )}
              {!loading && currentInsight && (
                <div className={styles.historyItem}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripMarkdownDividers(currentInsight.summary_text)}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AISidebar;
